import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { Link } from '@inertiajs/react';
import { motion, useReducedMotion } from 'framer-motion';
import { Search } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Agent {
    id: number;
    first_name: string;
    last_name: string;
    full_name: string;
    role: string;
    slug: string;
    main_image: string;
    is_luxury: boolean;
}

interface PaginatedAgents {
    data: Agent[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
}

interface TheTeamProps {
    luxuryCircle: Agent[];
    agents: PaginatedAgents;
}

/* ------------------------------------------------------------------ */
/*  Animation Tokens                                                   */
/* ------------------------------------------------------------------ */

const EASE = [0.22, 1, 0.36, 1] as const;

/* ------------------------------------------------------------------ */
/*  AgentCard                                                          */
/* ------------------------------------------------------------------ */

function AgentCard({ agent }: { agent: Agent }) {
    return (
        <Link href={`/the-team/${agent.slug}`} className="group relative block aspect-[3/4] overflow-hidden rounded-sm">
            {agent.main_image ? (
                <img
                    src={agent.main_image}
                    alt={agent.full_name}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
                />
            ) : (
                <div className="flex h-full w-full items-center justify-center bg-[#0e2727]/10">
                    <span className="lux-didot lux-didot-lg text-[#0e2727]/20">
                        {agent.first_name?.[0]}
                        {agent.last_name?.[0]}
                    </span>
                </div>
            )}
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 pt-12 pb-3 sm:px-4 sm:pt-16 sm:pb-4">
                <p className="lux-didot lux-didot-sm text-[#F5F5F5]">{agent.full_name}</p>
                {agent.role && <p className="lux-caps lux-caps-sm text-[#F5F5F5]/60">{agent.role}</p>}
            </div>
        </Link>
    );
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function TheTeam({ luxuryCircle, agents: initialAgents }: TheTeamProps) {
    const shouldReduceMotion = useReducedMotion();

    const [allAgents, setAllAgents] = useState<Agent[]>(initialAgents.data);
    const [currentPage, setCurrentPage] = useState(initialAgents.current_page);
    const [hasMore, setHasMore] = useState(currentPage < initialAgents.last_page);
    const [loading, setLoading] = useState(false);

    /* -- Animation helpers ----------------------------------------- */

    const inViewProps = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 20 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true, margin: '-50px' as const },
                  transition: { duration: 0.7, ease: EASE, delay },
              };

    const cardInViewProps = (delay = 0) =>
        shouldReduceMotion
            ? {}
            : {
                  initial: { opacity: 0, y: 16 } as const,
                  whileInView: { opacity: 1, y: 0 } as const,
                  viewport: { once: true },
                  transition: { duration: 0.5, ease: EASE, delay },
              };

    /* -- Search ---------------------------------------------------- */

    const [query, setQuery] = useState('');
    const [searchResults, setSearchResults] = useState<Agent[] | null>(null);
    const [searching, setSearching] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (query.length < 2) {
            setSearchResults(null);
            return;
        }

        setSearching(true);
        debounceRef.current = setTimeout(async () => {
            try {
                const res = await fetch(`/api/the-team/search?q=${encodeURIComponent(query)}`);
                if (!res.ok) return;
                const data = await res.json();
                setSearchResults(data.items);
            } catch {
                // Network error — silently fail
            } finally {
                setSearching(false);
            }
        }, 300);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [query]);

    const clearSearch = () => {
        setQuery('');
        setSearchResults(null);
    };

    const isSearching = query.length >= 2;

    /* -- Load More ------------------------------------------------- */

    const loadMore = useCallback(async () => {
        if (loading || !hasMore) return;
        setLoading(true);

        try {
            const nextPage = currentPage + 1;
            const res = await fetch(`/api/the-team/load-more?page=${nextPage}`);
            if (!res.ok) return;
            const data = await res.json();

            setAllAgents((prev) => [...prev, ...data.items]);
            setCurrentPage(data.current_page);
            setHasMore(data.has_more_pages);
        } catch {
            // Network error — user can retry via button
        } finally {
            setLoading(false);
        }
    }, [loading, hasMore, currentPage]);

    /* -- Render ---------------------------------------------------- */

    return (
        <PublicLayout
            headerConfig={{
                position: 'fixed',
                scrollBehavior: 'alwaysVisible',
                textColor: '#F5F5F5',
                background: 'transparent',
            }}
        >
            <MetaHead
                title="Our Advisors | LuxRally Travel"
                description="Meet the LuxRally Travel team — luxury travel advisors dedicated to crafting your perfect journey."
            />

            {/* ============================================================ */}
            {/*  SEARCH                                                      */}
            {/* ============================================================ */}

            <section className="nav-px bg-[#0b2222] pt-[calc(65px+4rem)] pb-16 md:pt-[calc(65px+6rem)] md:pb-24">
                <motion.div
                    initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
                    animate={shouldReduceMotion ? false : { opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
                    className="mx-auto max-w-3xl"
                >
                    <p className="lux-caps lux-caps-sm mb-8 text-center tracking-[0.3em] text-[#F5F5F5]/50">Search Our Advisors</p>
                    <div className="mx-auto flex w-fit max-w-full items-center justify-center gap-3 border-b border-[#F5F5F5]/25 pb-4 transition-colors focus-within:border-[#F5F5F5]/60">
                        <Search className="h-5 w-5 flex-shrink-0 text-[#F5F5F5]/45" strokeWidth={1.5} />
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Looking for someone specific?"
                            aria-label="Search advisors"
                            size={28}
                            className="lux-didot lux-didot-md w-auto min-w-0 bg-transparent text-[#F5F5F5] placeholder:text-[#F5F5F5]/30 focus:outline-none"
                        />
                    </div>
                </motion.div>
            </section>

            {/* ============================================================ */}
            {/*  SEARCH RESULTS                                              */}
            {/* ============================================================ */}

            {isSearching && (
                <section className="nav-px bg-[#fdfaf3] py-20 sm:py-24 lg:py-32">
                    <div className="mx-auto mb-12 max-w-3xl text-center sm:mb-16">
                        <span className="lux-caps lux-caps-sm text-[#0e2727]/60">
                            {searching
                                ? 'SEARCHING\u2026'
                                : searchResults
                                  ? `${searchResults.length} RESULT${searchResults.length !== 1 ? 'S' : ''}`
                                  : 'SEARCHING\u2026'}
                        </span>
                        <h2 className="lux-didot lux-didot-lg mt-4 text-[#0e2727]">
                            {searching
                                ? `Searching for \u201C${query}\u201D`
                                : searchResults?.length
                                  ? `Results for \u201C${query}\u201D`
                                  : `No advisors found for \u201C${query}\u201D`}
                        </h2>
                        {!searching && searchResults?.length === 0 && (
                            <p className="lux-body lux-body-lg mt-4 text-[#0e2727]/60">
                                Try a different name or{' '}
                                <button onClick={clearSearch} className="underline underline-offset-2 hover:text-[#0e2727]">
                                    clear your search
                                </button>
                            </p>
                        )}
                    </div>

                    {searchResults && searchResults.length > 0 && (
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                            {searchResults.map((agent, i) => (
                                <motion.div key={agent.id} {...cardInViewProps(Math.min(i, 19) * 0.05)}>
                                    <div className="overflow-hidden rounded-sm border border-[#0e2727]/10">
                                        <AgentCard agent={agent} />
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </section>
            )}

            {/* ============================================================ */}
            {/*  LUXURY CIRCLE                                               */}
            {/* ============================================================ */}

            {!isSearching && luxuryCircle.length > 0 && (
                <section className="nav-px bg-[#0e2727] pt-12 pb-20 sm:pt-14 sm:pb-24 lg:pt-16 lg:pb-32">
                    <div className="mx-auto mb-12 max-w-3xl text-center sm:mb-16">
                        <motion.div {...inViewProps()} className="flex items-center justify-center text-[#F5F5F5]/60">
                            <svg viewBox="0 0 130 28" fill="currentColor" aria-label="Elite" className="h-3.5 w-auto sm:h-4">
                                <text
                                    x="0"
                                    y="27"
                                    fontFamily="ui-sans-serif, system-ui, sans-serif"
                                    fontWeight="800"
                                    fontSize="38"
                                    letterSpacing="0.12em"
                                    fontStyle="italic"
                                >
                                    ELITE
                                </text>
                            </svg>
                        </motion.div>
                        <motion.h2 {...inViewProps(0.1)} className="lux-didot lux-didot-lg mt-4 text-[#F5F5F5]">
                            Our most distinguished advisors
                        </motion.h2>
                        <motion.p {...inViewProps(0.2)} className="lux-body lux-body-lg mx-auto mt-4 max-w-2xl text-[#F5F5F5]/75">
                            Hand-selected for their extraordinary expertise, Elite Advisors represent the pinnacle of personalized travel.
                        </motion.p>
                    </div>

                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {luxuryCircle.map((agent, i) => (
                            <motion.div key={agent.id} {...cardInViewProps(i * 0.08)}>
                                <AgentCard agent={agent} />
                            </motion.div>
                        ))}
                    </div>
                </section>
            )}

            {/* ============================================================ */}
            {/*  ALL AGENTS                                                  */}
            {/* ============================================================ */}

            {!isSearching && (
                <section className="nav-px bg-[#fdfaf3] py-20 sm:py-24 lg:py-32">
                    <div className="mx-auto mb-12 max-w-3xl text-center sm:mb-16">
                        <motion.span {...inViewProps()} className="lux-caps lux-caps-sm text-[#0e2727]/60">
                            OUR ADVISORS
                        </motion.span>
                        <motion.h2 {...inViewProps(0.1)} className="lux-didot lux-didot-lg mt-4 text-[#0e2727]">
                            Meet the full team
                        </motion.h2>
                    </div>

                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                        {allAgents.map((agent, i) => (
                            <motion.div key={agent.id} {...cardInViewProps(Math.min(i, 19) * 0.05)}>
                                <div className="overflow-hidden rounded-sm border border-[#0e2727]/10">
                                    <AgentCard agent={agent} />
                                </div>
                            </motion.div>
                        ))}
                    </div>

                    {/* Load More */}
                    {hasMore && (
                        <div className="mt-12 text-center">
                            <button
                                onClick={loadMore}
                                disabled={loading}
                                className="lux-caps lux-caps-sm rounded-sm border border-[#0e2727] px-6 py-2 text-[#0e2727] transition-colors hover:bg-[#0e2727] hover:text-[#F5F5F5] disabled:opacity-50"
                            >
                                {loading ? 'Loading\u2026' : 'Load More'}
                            </button>
                        </div>
                    )}
                </section>
            )}

            {/* ============================================================ */}
            {/*  CLOSING CTA                                                 */}
            {/* ============================================================ */}

            <section className="nav-px bg-[#0e2727] py-20 sm:py-24 lg:py-32">
                <div className="mx-auto max-w-2xl text-center">
                    <motion.span {...inViewProps()} className="lux-caps lux-caps-sm text-[#F5F5F5]/60">
                        GET STARTED
                    </motion.span>
                    <motion.h2 {...inViewProps(0.1)} className="lux-didot lux-didot-lg mt-4 text-[#F5F5F5]">
                        Ready to plan your journey?
                    </motion.h2>
                    <motion.p {...inViewProps(0.2)} className="lux-body lux-body-lg mt-4 text-[#F5F5F5]/75">
                        Connect with one of our advisors and start planning the trip of a lifetime.
                    </motion.p>
                    <motion.div {...inViewProps(0.3)} className="mt-8">
                        <Link
                            href="/clients"
                            className="lux-caps lux-caps-sm inline-block rounded-sm border border-[#F5F5F5]/40 px-6 py-2 text-[#F5F5F5] transition-colors hover:bg-[#F5F5F5] hover:text-[#0e2727]"
                        >
                            Become a Client
                        </Link>
                    </motion.div>
                </div>
            </section>
        </PublicLayout>
    );
}
